import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RiArrowDownSLine, RiArrowUpSLine, RiSearchLine } from '@remixicon/react';

export interface AppSelectSearchableOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface AppSelectSearchableProps {
  value: string;
  onChange: (value: string) => void;
  options: AppSelectSearchableOption[];
  placeholder?: string;
  isSearchable?: boolean;
  label?: string;
  disabled?: boolean;
}

let uid = 0;
const nextId = () => {
  uid += 1;
  return uid;
};

export const AppSelectSearchable = ({
  value,
  onChange,
  options,
  placeholder = 'Seleccionar',
  isSearchable = false,
  label,
  disabled = false,
}: AppSelectSearchableProps) => {
  const [search, setSearch] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Id estable para asociar aria-* entre trigger, listbox y opciones
  const baseId = useRef(`app-select-searchable-${nextId()}`).current;
  const listboxId = `${baseId}-listbox`;

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const optionsFiltered = useCallback(() => {
    if (search === '') return options;
    const searchLower = search.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchLower),
    );
  }, [search, options]);

  const filtered = optionsFiltered();

  const optionId = (index: number) => `${baseId}-option-${index}`;

  const firstEnabledIndex = useCallback(
    (list: AppSelectSearchableOption[]) =>
      list.findIndex((option) => !option.disabled),
    [],
  );

  const openDropdown = () => {
    // A disabled select neither opens nor toggles: single guard covers click and keyboard.
    if (disabled) return;
    const next = !showOptions;
    setShowOptions(next);
    if (next) {
      setSearch('');
      // Posiciona el activo en la opción seleccionada o la primera habilitada
      const selectedIdx = options.findIndex((o) => o.value === value);
      setActiveIndex(
        selectedIdx >= 0 && !options[selectedIdx]?.disabled
          ? selectedIdx
          : firstEnabledIndex(options),
      );
    } else {
      setActiveIndex(-1);
    }
  };

  const closeDropdown = () => {
    setShowOptions(false);
    setActiveIndex(-1);
  };

  const handleSelect = (option: AppSelectSearchableOption) => {
    if (option.disabled) return;
    onChange(option.value);
    closeDropdown();
  };

  // Al abrir con buscador, enfocar el input; el activedescendant vive en el input
  useEffect(() => {
    if (showOptions && isSearchable) {
      searchInputRef.current?.focus();
    }
  }, [showOptions, isSearchable]);

  // Cerrar al hacer clic fuera del componente (patrón fiable, sin depender de blur/hover)
  useEffect(() => {
    if (!showOptions) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptions]);

  // Reajustar el índice activo cuando cambia el filtro
  useEffect(() => {
    if (!showOptions) return;
    setActiveIndex((prev) => {
      if (prev >= 0 && prev < filtered.length && !filtered[prev]?.disabled) {
        return prev;
      }
      return firstEnabledIndex(filtered);
    });
  }, [search, showOptions]);

  // Mantener la opción activa visible dentro del scroll
  useEffect(() => {
    if (!showOptions || activeIndex < 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `#${CSS.escape(optionId(activeIndex))}`,
    );
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, showOptions]);

  const moveActive = (direction: 1 | -1) => {
    if (filtered.length === 0) return;
    setActiveIndex((prev) => {
      let idx = prev;
      for (let step = 0; step < filtered.length; step += 1) {
        idx = (idx + direction + filtered.length) % filtered.length;
        if (!filtered[idx]?.disabled) return idx;
      }
      return prev;
    });
  };

  const moveToEdge = (edge: 'first' | 'last') => {
    if (filtered.length === 0) return;
    if (edge === 'first') {
      setActiveIndex(firstEnabledIndex(filtered));
      return;
    }
    for (let i = filtered.length - 1; i >= 0; i -= 1) {
      if (!filtered[i]?.disabled) {
        setActiveIndex(i);
        return;
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!showOptions) {
          openDropdown();
        } else {
          moveActive(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!showOptions) {
          openDropdown();
        } else {
          moveActive(-1);
        }
        break;
      case 'Home':
        if (showOptions) {
          event.preventDefault();
          moveToEdge('first');
        }
        break;
      case 'End':
        if (showOptions) {
          event.preventDefault();
          moveToEdge('last');
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (!showOptions) {
          openDropdown();
        } else if (activeIndex >= 0 && filtered[activeIndex]) {
          handleSelect(filtered[activeIndex]);
        }
        break;
      case ' ':
        // Con buscador activo, el espacio debe escribirse en el input
        if (!isSearchable || !showOptions) {
          event.preventDefault();
          if (!showOptions) {
            openDropdown();
          } else if (activeIndex >= 0 && filtered[activeIndex]) {
            handleSelect(filtered[activeIndex]);
          }
        }
        break;
      case 'Escape':
        if (showOptions) {
          event.preventDefault();
          closeDropdown();
        }
        break;
      case 'Tab':
        if (showOptions) closeDropdown();
        break;
      default:
        break;
    }
  };

  const activeDescendant =
    showOptions && activeIndex >= 0 && filtered[activeIndex]
      ? optionId(activeIndex)
      : undefined;

  return (
    <label className="flex flex-col text-left text-sm text-shark-800 w-full">
      {label}
      <div
        ref={containerRef}
        className={`flex flex-col relative min-full ${label ? 'mt-1' : ''}`}
      >
        <button
          type="button"
          role="combobox"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={showOptions}
          aria-controls={listboxId}
          aria-activedescendant={!isSearchable ? activeDescendant : undefined}
          aria-label={
            selectedOption
              ? `Seleccionado: ${selectedOption.label}`
              : 'Seleccionar opción'
          }
          onKeyDown={handleKeyDown}
          className={`flex items-center gap-2 bg-white border border-ebony-950/15 rounded-lg h-11 px-3.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#568C13] ${
            disabled
              ? 'cursor-not-allowed opacity-60 bg-gray-50'
              : 'cursor-pointer'
          }`}
          onClick={openDropdown}
        >
          <span className="truncate text-left flex-1">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          {showOptions ? (
            <RiArrowUpSLine size={20} color="#475569" aria-hidden="true" />
          ) : (
            <RiArrowDownSLine size={20} color="#475569" aria-hidden="true" />
          )}
        </button>

        {showOptions && (
          <div className="bg-white rounded-lg mt-1 shadow-sm absolute top-11 z-10 w-full border border-gray-200">
            {isSearchable && (
              <>
                <div className="flex w-full items-center px-3 py-2 gap-3">
                  <RiSearchLine size={20} color="#94A3B8" aria-hidden="true" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    aria-label="Buscar opción"
                    role="searchbox"
                    aria-controls={listboxId}
                    aria-activedescendant={activeDescendant}
                    placeholder="Buscar..."
                    className="w-full border-0 outline-none focus:outline-none focus:ring-0"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <hr />
              </>
            )}

            {filtered.length === 0 ? (
              <p className="p-3" role="status" aria-live="polite">
                Sin resultados
              </p>
            ) : (
              <ul
                ref={listRef}
                id={listboxId}
                role="listbox"
                className="w-full max-h-[280px] overflow-y-auto"
              >
                {filtered.map((option, index) => {
                  const isSelected = option.value === value;
                  const isActive = index === activeIndex;
                  return (
                    <li
                      key={option.value}
                      id={optionId(index)}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled}
                      // Seleccionar en mousedown y prevenir el default: evita que
                      // el blur del buscador o un re-render del padre entre
                      // mousedown y click impidan el click, dejando el dropdown
                      // abierto. closeDropdown() en handleSelect garantiza el cierre.
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleSelect(option)
                      }}
                      onMouseEnter={() =>
                        !option.disabled && setActiveIndex(index)
                      }
                      className={`flex items-center px-3 h-10 text-sm truncate
                        ${option.disabled
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-700 cursor-pointer hover:bg-[#F1F9EC]'}
                        ${isActive && !option.disabled ? 'bg-[#F1F9EC]' : ''}
                        ${isSelected ? 'bg-[#F1F9EC] font-medium' : ''}`}
                    >
                      {option.label}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </label>
  );
};
